import { format } from "date-fns";
import { twoFixedDigit } from "../../../../utils/twoFixedDigit";
import { snakeCaseToHumanReadable } from "../../../../utils/snakeCaseToHumanReadable";

export function formatChartLabel({
  value,
  isSameDayRange,
}: {
  value: number;
  isSameDayRange: boolean;
}) {
  return format(new Date(value), isSameDayRange ? "h a" : "MMM d");
}

export function percentageFormatter(value: number) {
  return value > 0.01 ? twoFixedDigit(value) : Number(value.toFixed(4));
}

const CARD_BRANDS = [
  { canonical: "Mastercard", patterns: ["MASTERCARD", "MASTER"] },
  { canonical: "Visa", patterns: ["VISA"] },
  { canonical: "Mada", patterns: ["MADA"] },
  { canonical: "American Express", patterns: ["AMEX", "AMERICANEXPRESS"] },
  { canonical: "Meeza", patterns: ["MEEZA"] },
  { canonical: "Discover", patterns: ["DISCOVER"] },
  { canonical: "JCB", patterns: ["JCB"] },
  { canonical: "UnionPay", patterns: ["UNIONPAY"] },
  { canonical: "Diners Club", patterns: ["DINERSCLUB", "DINERS"] },
  { canonical: "Maestro", patterns: ["MAESTRO"] },
];

export function normalizeCardBrand(label: string): string {
  if (!label || label.trim() === "") return "unknown";

  const cleaned = label.toUpperCase().replace(/[^A-Z0-9]/g, "");

  // Exact match first
  for (const brand of CARD_BRANDS) {
    if (brand.patterns.some((p) => p === cleaned)) {
      return brand.canonical;
    }
  }

  // Fuzzy match
  for (const brand of CARD_BRANDS) {
    if (
      brand.patterns.some((p) => cleaned.includes(p) || p.includes(cleaned))
    ) {
      return brand.canonical;
    }
  }

  // Return original with title case if no match
  return snakeCaseToHumanReadable(label).toLowerCase();
}

export function aggregateBrandData(
  data: Array<{ label: string; value: number }>
): Array<{ label: string; value: number }> {
  const aggregated = new Map<string, number>();

  for (const item of data) {
    const normalizedLabel = normalizeCardBrand(item.label);
    aggregated.set(
      normalizedLabel,
      (aggregated.get(normalizedLabel) || 0) + item.value
    );
  }

  return Array.from(aggregated.entries()).map(([label, value]) => ({
    label,
    value,
  }));
}
